import { Check } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * WizardContainer — multi-step wizard shell for MFA enroll, Factor builder, etc.
 * Pencil 7vKRK.
 *
 * Props:
 *  - steps: ordered list { key, label, description? }
 *  - currentStep: index into steps
 *  - onNext / onBack / onComplete: nav callbacks
 *  - children: the active step's body
 */
export interface WizardStep {
  key: string
  label: string
  description?: string
}

export interface WizardContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: WizardStep[]
  currentStep: number
  onBack?: () => void
  onNext?: () => void
  onComplete?: () => void
  backLabel?: string
  nextLabel?: string
  completeLabel?: string
  canProceed?: boolean
  submitting?: boolean
}

export const WizardContainer = React.forwardRef<HTMLDivElement, WizardContainerProps>(
  (
    {
      className,
      steps,
      currentStep,
      onBack,
      onNext,
      onComplete,
      backLabel = 'Back',
      nextLabel = 'Next',
      completeLabel = 'Finish',
      canProceed = true,
      submitting,
      children,
      ...props
    },
    ref,
  ) => {
    const isLast = currentStep === steps.length - 1
    return (
      <div ref={ref} className={cn('flex flex-col gap-6', className)} {...props}>
        {/* Stepper */}
        <ol className="flex items-center gap-3">
          {steps.map((step, i) => {
            const state = i < currentStep ? 'done' : i === currentStep ? 'current' : 'pending'
            return (
              <React.Fragment key={step.key}>
                <li className="flex items-center gap-3">
                  {/*
                    shrink-0 + aspect-square: keep the indicator a perfect
                    circle even when sibling labels wrap to multiple lines
                    inside the flex row. Without shrink-0 the connector's
                    flex-1 would compress the circle horizontally into an
                    oval; without aspect-square, browser rounding can still
                    drift the width/height by a fractional pixel.
                  */}
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 aspect-square items-center justify-center rounded-pill text-caption font-semibold transition-colors',
                      state === 'done' && 'bg-ia-success text-ia-text-inverse',
                      state === 'current' &&
                        'bg-primary text-primary-foreground ring-4 ring-ia-brand-glow',
                      state === 'pending' && 'bg-ia-elevated text-ia-text-tertiary',
                    )}
                  >
                    {state === 'done' ? <Check size={16} strokeWidth={2.5} /> : i + 1}
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span
                      className={cn(
                        'text-body-sm font-medium',
                        state === 'current' ? 'text-ia-text' : 'text-ia-text-tertiary',
                      )}
                    >
                      {step.label}
                    </span>
                    {step.description && (
                      <span className="text-caption text-ia-text-tertiary">{step.description}</span>
                    )}
                  </div>
                </li>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-px bg-ia-border min-w-[32px]" />
                )}
              </React.Fragment>
            )
          })}
        </ol>

        {/* Step body */}
        <div className="rounded-lg border border-ia-border bg-card p-6">{children}</div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" disabled={currentStep === 0 || submitting} onClick={onBack}>
            {backLabel}
          </Button>
          <Button
            disabled={!canProceed}
            loading={submitting}
            onClick={isLast ? onComplete : onNext}
          >
            {isLast ? completeLabel : nextLabel}
          </Button>
        </div>
      </div>
    )
  },
)
WizardContainer.displayName = 'WizardContainer'
